import { Controller } from '@hotwired/stimulus';

// Connects to data-controller="chat-taxonomy-validator"
//
// Live-resolver for the sub-context workbench. Resolves the sub-context's
// selector_cast_path against a sample chat_cast and reports which of the
// linked ChatDocuments match the resolved selector_value.
//
// Targets:
//   - castSelect: <select> with chat_cast ids
//   - results: container that receives the rendered HTML
//   - status: small text indicator (loading / OK / error)
//
// Values:
//   - url: validate_cast endpoint (GET, returns JSON)
export default class extends Controller {
  static targets = ['castSelect', 'results', 'status'];
  static values = { url: String };

  connect() {
    if (this.hasCastSelectTarget && this.castSelectTarget.value) {
      this.validate();
    }
  }

  async validate() {
    const castId = this.hasCastSelectTarget ? this.castSelectTarget.value : '';
    if (!castId) {
      this.setStatus('Bitte einen Sample-Cast wählen.', 'warning');
      this.resultsTarget.innerHTML = '';
      return;
    }

    this.setStatus('Validiere…', 'info');
    try {
      const url = `${this.urlValue}?cast_id=${encodeURIComponent(castId)}`;
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      if (data.error) {
        this.setStatus(data.error, 'error');
        this.resultsTarget.innerHTML = '';
        return;
      }

      this.render(data);
      const matchCount = (data.documents || []).filter((d) => d.matches).length;
      this.setStatus(`Auflösung OK — ${matchCount} passende(s) Dokument(e).`, matchCount > 0 ? 'success' : 'warning');
    } catch (err) {
      this.setStatus(`Fehler: ${err.message}`, 'error');
    }
  }

  render(data) {
    const selector = data.selector || {};
    const documents = data.documents || [];

    const selectorValueCell = selector.path
      ? selector.found
        ? `<code>${this.escape(JSON.stringify(selector.value))}</code>`
        : '<span class="text-danger">— im Cast nicht gefunden —</span>'
      : '<span class="text-muted">kein Selector</span>';

    const docsRows = documents.length === 0
      ? '<tr><td colspan="4" class="text-muted">Keine Dokumente verlinkt.</td></tr>'
      : documents.map((doc) => `
          <tr style="${doc.matches ? 'background: #e8f5e9;' : ''}">
            <td>${doc.matches ? '<i class="fa fa-check text-success" title="passt"></i>' : '<span class="text-muted">–</span>'}</td>
            <td>${this.escape(doc.locale)}</td>
            <td>${doc.selector_value ? `<code>${this.escape(doc.selector_value)}</code>` : '<span class="text-muted">—</span>'}</td>
            <td><code style="font-size: 11px;">${this.escape(doc.relative_path)}</code>${doc.archived ? ' <span class="text-muted">(archiviert)</span>' : ''}</td>
          </tr>
        `).join('');

    this.resultsTarget.innerHTML = `
      <div class="alert alert-light" style="background: #f8f9fa; border: 1px solid #dee2e6; padding: 10px;">
        <small class="text-muted">Sample: ${this.escape(data.cast.name)} (${this.escape(String(data.cast.dob))})</small>
        <table class="table table-sm" style="margin-top: 8px; font-size: 12px;">
          <thead>
            <tr><th>Selector-Pfad</th><th>Alias</th><th>Aufgelöster Wert</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>${selector.path ? `<code>${this.escape(selector.path)}</code>` : '<span class="text-muted">—</span>'}</td>
              <td>${selector.alias ? `<code>${this.escape(selector.alias)}</code>` : '<span class="text-muted">—</span>'}</td>
              <td>${selectorValueCell}</td>
            </tr>
          </tbody>
        </table>
        <h6 style="margin: 12px 0 4px;">Dokumenten-Treffer</h6>
        <table class="table table-sm" style="font-size: 12px;">
          <thead>
            <tr><th style="width: 40px;"></th><th style="width: 60px;">Locale</th><th style="width: 120px;">Selector-Wert</th><th>Pfad</th></tr>
          </thead>
          <tbody>
            ${docsRows}
          </tbody>
        </table>
      </div>
    `;
  }

  setStatus(message, kind) {
    if (!this.hasStatusTarget) return;
    const colors = { info: '#0c5460', success: '#155724', warning: '#856404', error: '#721c24' };
    this.statusTarget.style.color = colors[kind] || '#333';
    this.statusTarget.textContent = message;
  }

  escape(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }
}
